import * as React from "react";
import { BaseModal } from "../../../atoms/base-modal";
import {
  AllStatuses,
  ApplicationEntity,
  ApplyStatus,
} from "../../../../application/domain/application/application-entity";
import { useCallback, useEffect, useState } from "react";
import {
  BaseTypeLiterals,
  matchType,
} from "../../../../application/domain/base/base-type";
import {
  ApplicationStatusTag,
  StatusRadios,
} from "../../../molecules/status-tag";
import {
  requirePaymentNotifWarning,
  requireStatusBackWarning,
} from "../../../../application/domain/application/apply-status-warning";
import { CharCount } from "../../../molecules/char-count";
import { ApplicationsConst } from "../../../../application/domain/application/const";
import classnames from "classnames";
import {
  SmartApplyApiError,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { Checkbox, TextArea } from "@graffer-inc/console-design-kit";

interface StatusUpdateModalProps {
  app: ApplicationEntity;
  isActive: boolean;
  onUpdateStatus: (
    appl: ApplyStatus,
    skipNotification: boolean,
    message?: string,
  ) => Promise<void>;
  onClose: () => void;
}

function match<R>(
  state: "SELECT" | "CONFIRM",
  matcher: BaseTypeLiterals<"SELECT" | "CONFIRM", R>,
) {
  return matcher[state];
}

export const StatusUpdateModal: React.FunctionComponent<
  StatusUpdateModalProps
> = ({ app, isActive, onUpdateStatus, onClose }) => {
  const [isWarnStatusBack, setIsWarnStatusBack] = useState(false);
  const [isWarnPaymentNotif, setIsWarnPaymentNotif] = useState(false);
  const setRequireWarning = useCallback(
    (next: ApplyStatus): void => {
      setIsWarnStatusBack(requireStatusBackWarning(app.status, next));
      if (app.hasRefundablePayment()) {
        setIsWarnPaymentNotif(requirePaymentNotifWarning(next));
      }
    },
    [app, setIsWarnStatusBack, setIsWarnPaymentNotif],
  );

  const [nextStatus, setNextStatus] = useState<ApplyStatus | "">("");
  const [skipNotification, setSkipNotification] = useState(false);
  const [pageStatus, setPageState] = useState<"SELECT" | "CONFIRM">("SELECT");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isActive) {
      // clear all state props when modal is closed
      setIsWarnStatusBack(false);
      setIsWarnPaymentNotif(false);
      setNextStatus("");
      setSkipNotification(false);
      setPageState("SELECT");
      setMessage("");
      setIsSubmitting(false);
    }
  }, [
    isActive,
    setIsWarnStatusBack,
    setIsWarnPaymentNotif,
    setNextStatus,
    setSkipNotification,
    setPageState,
    setMessage,
    setIsSubmitting,
  ]);

  const statusText = matchType(nextStatus, {
    "": undefined,
    UNTREATED: undefined,
    WIP: undefined,
    CLOSED_COMPLETED: "完了",
    CLOSED_REJECTED: "差し戻し",
    CLOSED_WITHDRAWN: "取下げ",
  });
  const shouldNotif = nextStatus.startsWith("CLOSED_");
  const changableStatuses = app.changableStatus();
  const filtered: ApplyStatus[] = AllStatuses.filter(
    (s) => !changableStatuses.includes(s),
  );
  const isEnableMessage = (): boolean => {
    return (
      !skipNotification &&
      (nextStatus === "CLOSED_REJECTED" ||
        nextStatus === "CLOSED_COMPLETED" ||
        nextStatus === "CLOSED_WITHDRAWN")
    );
  };
  const validateMessage = (): boolean => {
    if (!isEnableMessage()) return true;
    return message.length <= ApplicationsConst.MESSAGE_MAX_LENGTH;
  };
  return (
    <BaseModal
      isActive={isActive}
      title={match(pageStatus, {
        SELECT: "ステータスを更新する",
        CONFIRM: "ステータスの更新内容の確認",
      })}
      onClose={onClose}
    >
      {match(pageStatus, {
        SELECT: (
          <>
            <section className="modal-card-body">
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label htmlFor="name" className="label">
                      現在
                    </label>
                    <div className="control">
                      <ApplicationStatusTag
                        hiddenCheckIcon
                        status={app.status}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="name" className="label">
                      変更先
                    </label>
                    <div className="control">
                      <StatusRadios
                        filtered={filtered}
                        current={nextStatus}
                        onChange={(s: ApplyStatus) => {
                          setRequireWarning(s);
                          setNextStatus(s);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {shouldNotif && (
                <Checkbox
                  label={"通知設定"}
                  description={
                    "申請者への通知をスキップする場合はチェックを入れてください。"
                  }
                  options={[
                    {
                      name: "申請者への通知をスキップする",
                      value: "skipNotification",
                    },
                  ]}
                  values={skipNotification ? ["skipNotification"] : []}
                  onChange={() => setSkipNotification((oldValue) => !oldValue)}
                />
              )}{" "}
              {isEnableMessage() && (
                <>
                  <TextArea
                    onChange={(e) => setMessage(e.target.value)}
                    label="申請者へのメッセージ"
                    description={explanation}
                    value={message}
                    errorMessages={[]}
                  />
                  <div className="level">
                    <div className="level-left">
                      {!validateMessage() && (
                        <p className="help is-danger">
                          メッセージは{ApplicationsConst.MESSAGE_MAX_LENGTH}
                          文字以内で入力してください
                        </p>
                      )}
                    </div>
                    <div className="level-right">
                      <CharCount
                        text={message}
                        maxLength={ApplicationsConst.MESSAGE_MAX_LENGTH}
                      />
                    </div>
                  </div>
                </>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button
                className={"button is-info is-fullwidth"}
                onClick={() => {
                  setPageState("CONFIRM");
                }}
                data-for-rpa-id={"status-update"}
                disabled={
                  !nextStatus || app.status === nextStatus || !validateMessage()
                }
              >
                更新内容の確認へ進む
              </button>
            </footer>
          </>
        ),
        CONFIRM: (
          <>
            <section className="modal-card-body">
              <div className="field is-horizontal">
                <div className="field-body">
                  <div className="field">
                    <label htmlFor="name" className="label">
                      現在
                    </label>
                    <div className="control">
                      <ApplicationStatusTag
                        status={app.status}
                        hiddenCheckIcon
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="name" className="label">
                      変更先
                    </label>
                    <div className="control">
                      <ApplicationStatusTag
                        status={nextStatus as ApplyStatus}
                        hiddenCheckIcon
                      />
                    </div>
                  </div>
                </div>
              </div>
              {(shouldNotif || (nextStatus && isWarnPaymentNotif)) && (
                <div className="notification has-text-weight-bold content">
                  <p className="mb-1">
                    ステータス更新時に以下の処理が実行されます。
                  </p>
                  <ul>
                    {app.hasRefundablePayment() &&
                      matchType(nextStatus, {
                        "": undefined,
                        UNTREATED: undefined,
                        WIP: undefined,
                        CLOSED_COMPLETED: undefined,
                        CLOSED_REJECTED: (
                          <li>
                            未返金の決済の返金処理
                            {app.hasPayeasyPayment() &&
                              "（※ペイジー支払いは返金されません）"}
                          </li>
                        ),
                        CLOSED_WITHDRAWN: (
                          <li>
                            未返金の決済の返金処理
                            {app.hasPayeasyPayment() &&
                              "（※ペイジー支払いは返金されません）"}
                          </li>
                        ),
                      })}
                    {shouldNotif && (
                      <li>
                        {skipNotification
                          ? `申請者への${statusText}通知メールをスキップする`
                          : `申請者への${statusText}通知メールの送信`}
                      </li>
                    )}
                  </ul>
                </div>
              )}
              {isWarnStatusBack && (
                <div className="notification is-danger is-light">
                  <p className="is-small">
                    ステータスを、以前の状態に戻す変更です。
                  </p>
                  <p className="is-small">
                    利用者からステータスが戻ったように認識される可能性があります。
                  </p>
                </div>
              )}
            </section>
            <footer className="modal-card-foot">
              <button
                className="button is-failure is-fullwidth"
                onClick={() => {
                  setPageState("SELECT");
                }}
                disabled={isSubmitting}
              >
                選択に戻る
              </button>
              <button
                className={classnames("button is-info is-fullwidth", {
                  "is-loading": isSubmitting,
                })}
                onClick={async () => {
                  if (isSubmitting) {
                    return;
                  }
                  if (nextStatus !== "") {
                    setIsSubmitting(true);
                    try {
                      await onUpdateStatus(
                        nextStatus,
                        skipNotification,
                        isEnableMessage() ? message : undefined,
                      );
                      toast.success("申請情報を更新しました。");
                      onClose();
                    } catch (e) {
                      let msg = "ステータスの更新に失敗しました。";
                      if (SmartApplyApiError.isVersionOutdated(e)) {
                        msg =
                          "申請情報の更新に失敗しました。画面を更新して再度実行してください。";
                      } else if (SmartApplyApiError.isRetryable(e)) {
                        msg =
                          "ネットワークエラーが発生しました。リトライをお試しください。";
                      } else {
                        reportUnexpectedError(e);
                      }
                      toast.error(msg);
                    } finally {
                      setIsSubmitting(false);
                    }
                  }
                }}
                disabled={isSubmitting}
              >
                この内容で更新する
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};

const explanation = [
  "入力したメッセージが以下に記載されます。未入力の場合は記載されません。",
  "・申請者の申請詳細画面",
  "・自動通知メール",
  "　※ 通知メール設定に{メッセージ}の指定が必要です",
].join("\n");
